import { makeStyles } from '@mui/styles';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import DataGridV2 from '../../../../components/DataGridV2';
import { apps, LOCAL_STORAGE_GRID_KEYS, } from '../../../../_constants';
import {  useMediaQuery, useTheme } from '@mui/material';
import { Cancel, CheckCircle, } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { INITIAL_ROW_SELECTION_MODEL } from '../../../../components/DataGridV2/constants';
import { formatDate, getLocalStorageItem, repairRequestDbStatuses, setLocalStorageItem, useCanLockedInspection, useInternetStatusDetector } from '../../../../_helpers';
import { getRepairRequestLocations } from '../../../../apis/surveyingApis';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { surveyActions } from '../../../../_actions';
import RepairRequestInfoModal from '../../components/RepairRequestInfoModal';
import { RepairRequestDetailModal } from '../../components/RepairRequestDetailModal';
import RepairRequestCustomRow from '../../components/RepairRequestCustomRow';
import { getIdsForGridSelectedRows } from '../../../../_helpers/getIdsForGridSelectedRows';
import { getSecondSignatureRepairRequestColumns } from './secondSignatureRepairRequestUtils';

const SecondSignatureRepairRequest = ({
    selectedInspection,
    loading,
    requests,
    ...props
}) => {
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();
    const { t, i18n,ready} = useTranslation();
    const [readOnly, setReadOnly] = useState(false);
    const classes = useStyles({ readOnly });
    const apiRef = useGridApiRef();
    const app = useSelector((state) => state.app);
    const { lng } = useSelector((state) => state.appConfig) || {}
    const keyPrefix = app === apps.aftercare ? "AFTERCARE" : "GENERAL";
    const { deleteRRLoading } = useSelector((state) => state.surveying);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const canLockedInspection = useCanLockedInspection();
    const isOnline = useInternetStatusDetector();
    const [locations, setLocations] = useState([]);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
    const [infoType, setInfoType] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_PAGINATION[keyPrefix], undefined
    ))
    const [isOpenInfoModal, setIsOpenInfoModal] = useState(false);
    const rrDbStatuses = useMemo(() => repairRequestDbStatuses(i18n, lng), [lng, i18n?.isInitialized, ready]);
       
    useEffect(() => {
        const fetchLocations = async () => {
            try {

                const locationRes = await getRepairRequestLocations();

                const locations = locationRes.data.map(x => ({
                    label: x.name,
                    value: x.name
                }));

                setLocations(locations);
            } catch (e) {
                console.error("Error fetching locations:", e);
            } finally {
            }
        };

        fetchLocations();
    }, []);

  
    useEffect(() => {
        if (selectedRequestId && !deleteRRLoading) {
            setSelectedRequestId(null);
        }
    }, [selectedRequestId, deleteRRLoading]);

    useEffect(() => {
        if (selectedInspection) {
            setReadOnly(
                selectedInspection.secondSignatureDate ||
                canLockedInspection ||
                !selectedInspection.hasRights
            );
        }
    }, [canLockedInspection, selectedInspection]);

  const detailDialogOpenHandler = (request) => {
        setIsOpenDetail(!isOpenDetail);
        setSelectedRequest(isOpenDetail ? null : request);
        !isOpenDetail && setRowSelectionModel(INITIAL_ROW_SELECTION_MODEL);
    };
const statusOptions= requests?.length ? [...new Set(requests.map((item) => item.status))] : [];

const columns = getSecondSignatureRepairRequestColumns({t,theme,keyPrefix,i18n,apiRef,detailDialogOpenHandler,locations,statusOptions,classes,rrDbStatuses})


    const handleRowClick = (selectedRow) => {
        if (selectedRow && isOnline && selectedRow.sync !== 0) {
            switch (app) {
                case apps.constructionQuality: {
                    history.push(
                        `/werk/${selectedInspection.projectNo}/kwaliteitsborging/opname/melding/${selectedRow.requestId}`
                    );
                    break;
                }
                case apps.aftercare: {
                    history.push(`/nazorg/opname/melding/${selectedRow.requestId}`);
                    break;
                }
                default: {
                    break;
                }
            }
        }
    };

    const onPaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel)
        setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_PAGINATION[keyPrefix],
            newPaginationModel
        );
    }, [keyPrefix])

    const getSecondSignatureRepairRequestForPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
        const userSelectedRows = isAllSelected ? requests : idsToExport.map(id => requests?.find(item => item?.requestId === id));
        const results = userSelectedRows?.map(item => {
            const result = {};
            Object.keys(item).forEach(key => {
                let value = item[key];
                const isDateField = columns.some(x => x.field === key && x.type === 'date');
                if (isDateField) {
                    value = item[key] ? formatDate(new Date(item[key])) : "";
                }
                result[key] = value;
            });

            return result;
        });

        return results;
    };

    const requestIds = useMemo(() => {
        return getIdsForGridSelectedRows(rowSelectionModel, requests)
    }, [rowSelectionModel, requests]);

    const selectedRows = requests.filter(r => requestIds.includes(r.requestId));

    const canApproveReject = requestIds.every((requestId) => {
        const request = requests.find((p) => p.requestId === requestId);
        return (
            request &&
            (!request.resolvers ||
                (request.resolvers &&
                    request.resolvers.every(
                        (r) =>
                            !r.workOrderNumber ||
                            (r.workOrderNumber && (r.status === 3 || r.status === 4))
                    )))
        );
    });

    const infoModalTogglerHandler = (type, request) => {
        if (selectedRows.length > 1) {
            dispatch(surveyActions.markCompleteRRActions(selectedRows));
        } else {
            setIsOpenDetail(false);
            isOpenInfoModal && setIsOpenDetail(false);
            setInfoType(isOpenInfoModal ? null : type);
            setIsOpenInfoModal((p) => !p);
            request =
                request ||
                (selectedRows.length > 0 &&
                    requests.find((a) => a.requestId === selectedRows[0].requestId));
            setSelectedRequest(request);
        }
    };

    const getToolbarProps = useMemo(() => {
        const hasSelectedRows = requestIds.length > 0
        const toolbarProps = {
            taskMenuListItems: hasSelectedRows
                ? [
                    {
                        title: t("general.button.reject"),
                        icon: (props) => <Cancel color='error'   {...props} />,
                        onClick: () => infoModalTogglerHandler("reject"),
                        disabled: (selectedRows && selectedRows.length > 1) || selectedRows.some(r => !r.completed && r.isRework) || !canApproveReject,
                        closeTaskMenu: true
                    },
                    {
                        title: t("general.approve"),
                        icon: (props) => <CheckCircle size="small" className={classes.colorSuccess}  {...props} />,
                        onClick: () => infoModalTogglerHandler("approve"),
                        disabled: selectedRows.some(r => r.completedByBuyer) || !canApproveReject,
                        closeTaskMenu: true
                    },
                ]
                : [],
        };
        return toolbarProps;
    }, [rowSelectionModel, t, selectedRows, canApproveReject,]);

    const handleCheckboxChange = useCallback((itemId) => {
        setRowSelectionModel((prevCheckedItems) => {
            const prevIds = Array.from(prevCheckedItems?.ids);
            let newIds = [];
            if (prevIds.includes(itemId)) {
                newIds = prevIds.filter((id) => id !== itemId);
            } else {
                newIds = [...prevIds, itemId];
            }
            return { ...prevCheckedItems, ids: new Set(newIds) };
        });
    }, []);

    const detailDialogCloseHandler = () => {
        setSelectedRequest(null);
        setIsOpenDetail(false);
    };
    return (
        <>
            <DataGridV2
                MobileCustomRowComponent={(props) => {
                    return <RepairRequestCustomRow
                        selectedCheckBoxes={Array.from(rowSelectionModel?.ids)}
                        handleCheckboxChange={handleCheckboxChange}
                        onRowClick={handleRowClick}
                        item={props?.row}
                        key={props.rowId}
                        canShowCheckBox={
                            !readOnly &&
                            selectedInspection &&
                            selectedInspection.isSecondSignatureInitiated
                        }
                    />
                }}
                localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_SEARCH_TEXT[keyPrefix]}
                localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_COLUMN_FILTER[keyPrefix]}
                localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_COLUMN_SORT[keyPrefix]}
                localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_COLUMN_ORDER[keyPrefix]}
                localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_COLUMN_WIDTH[keyPrefix]}
                localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_COLUMN_VISIBILITY[keyPrefix]}
                localDataGridViewIdentifier={LOCAL_STORAGE_GRID_KEYS.SECOND_SIGNATURE_REPAIR_REQUESTS_DATAGRID_VIEW[keyPrefix]}
                apiRef={apiRef}
                checkboxSelection={
                    !readOnly &&
                    selectedInspection &&
                    selectedInspection.isSecondSignatureInitiated
                }
                rows={requests}
                columns={columns}
                loading={loading}
                pagination
                filterMode="client"
                customRowHeight={isMobile ? 80 : 62}
                {...(!!paginationModel && { paginationModel })}
                onPaginationModelChange={onPaginationModelChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    setRowSelectionModel(newRowSelectionModel);
                }}
                toolbarProps={{
                    ...getToolbarProps,
                    selectedRows: rowSelectionModel,
                    getData: getSecondSignatureRepairRequestForPrintAndDownload,
                    title: t('general.repairRequests'),

                }}
                onRowClick={(params) => {
                    const requestId = params.row?.requestId;
                    const selectedRow = requests?.find((x) => x.requestId === requestId);
                    if (selectedRow) {
                        handleRowClick(selectedRow);
                    }
                }}
            />
            {isOpenInfoModal && (
                <RepairRequestInfoModal
                    canDetailView={null}
                    selectedRequest={selectedRequest}
                    handleClose={infoModalTogglerHandler}
                    open={isOpenInfoModal}
                    infoType={infoType}
                />
            )}
            {selectedRequest && isOpenDetail && (
                <RepairRequestDetailModal
                    selectedInspection={selectedInspection}
                    readOnly={
                        readOnly ||
                        (selectedInspection &&
                            !selectedInspection.isSecondSignatureInitiated)
                    }
                    open={isOpenDetail}
                    handleRequest={infoModalTogglerHandler}
                    handleClose={detailDialogCloseHandler}
                    requestId={selectedRequest.requestId}
                />
            )}
        </>

    )

}
const useStyles = makeStyles((theme) => ({
    colorSuccess: {
        padding: theme.spacing(0.2),
        color: theme.palette.success.main,
    },
    colorPrimary: {
        color: theme.palette.primary.main,
    },

}));
export default memo(SecondSignatureRepairRequest)
